import React, { FC, useCallback, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { SMBTemplateFactory } from "@xapp/stentor-templates";

import { LoadingSpinner, fetchDetails } from "@xapp/components";

import { useGetAppInfoQuery, useUpdateApplicationMutation } from "generated/graphql";
import { useWrapUrql } from "utils/graphql/useWrapUrql";
import { prepareDataGQL } from "utils/graphql";

import { HighValueLeadDescriptionStep } from "../../components/setup/HighValueLeadDescriptionStep";
import { ClientApp } from "models";

export interface HighValueLeadDescriptionStepContainerProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Callback when user clicks continue
     */
    onNext?: () => void;
    /**
     * Callback when user clicks back
     */
    onBack?: () => void;
    /**
     * Optional app data if already loaded
     */
    app?: ClientApp;
}

export const HighValueLeadDescriptionStepContainer: FC<HighValueLeadDescriptionStepContainerProps> = (props) => {
    const { aside, onNext, onBack, className, style } = props;
    const { appId } = useParams<{ appId: string }>();

    const [saving, setSaving] = useState(false);
    const [generatedDescription, setGeneratedDescription] = useState<string | undefined>();

    const [getAppInfo] = useGetAppInfoQuery({
        pause: !appId || !!props.app,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
        },
    });

    const updateApplication = useWrapUrql(useUpdateApplicationMutation(), {
        onSuccessMessage: "High value lead description saved",
    });

    // Use provided app or fetch from query
    const app = props.app || getAppInfo.data?.app;

    // Generate the high value lead description based on place types or template type
    useEffect(() => {
        const generateDescription = async () => {
            // First, try to get place details if we have a placeId
            if (app?.places && app.places.length > 0 && app.places[0]?.placeId) {
                const placeId = app.places[0].placeId;

                try {
                    const placeDetails = await fetchDetails(placeId);

                    if (placeDetails?.types && placeDetails.types.length > 0) {
                        const description = SMBTemplateFactory.generateHighValueLeadDescription(placeDetails.types);
                        setGeneratedDescription(description);
                        return;
                    }
                } catch (error) {
                    console.error("Error fetching place details:", error);
                    // Fall through to use template
                }
            }

            // Fallback to template from beta.onboarding or templateType
            const templateName = app?.beta?.onboarding?.template?.name;
            const jobType = templateName || app?.templateType;

            if (!jobType) {
                setGeneratedDescription(undefined);
                return;
            }

            try {
                const description = SMBTemplateFactory.generateHighValueLeadDescription(jobType);
                setGeneratedDescription(description);
            } catch (error) {
                console.error("Error generating high value lead description:", error);
                setGeneratedDescription(undefined);
            }
        };

        if (app) {
            generateDescription();
        }
    }, [app]);

    const handleNext = useCallback(
        async (data: Pick<ClientApp, "appId" | "businessHighValueLeadDescription">) => {
            setSaving(true);

            try {
                const cleaned = prepareDataGQL({
                    businessHighValueLeadDescription: data.businessHighValueLeadDescription,
                });

                await updateApplication({
                    appId,
                    app: cleaned,
                });

                onNext?.();
            } catch (error) {
                console.error("Error saving high value lead description:", error);
            } finally {
                setSaving(false);
            }
        },
        [appId, updateApplication, onNext]
    );

    if (getAppInfo.fetching && !app) {
        return <LoadingSpinner loadingText={`Loading ${appId}`} />;
    }

    return (
        <HighValueLeadDescriptionStep
            style={style}
            className={className}
            aside={aside}
            app={app}
            generatedDescription={generatedDescription}
            loading={getAppInfo.fetching}
            saving={saving}
            onNext={handleNext}
            onBack={onBack}
        />
    );
};
